import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'

export const Analytics: React.FC = () => {
  const { t } = useTranslation()
  const [days, setDays] = useState(30)
  const [activeTab, setActiveTab] = useState<'general' | 'ai'>('general')

  const { data: dashboardData } = useQuery({
    queryKey: ['analytics-dashboard'],
    queryFn: async () => {
      const response = await api.get('/analytics/dashboard')
      return response.data.data
    },
  })

  const { data: conversationsData } = useQuery({
    queryKey: ['analytics-conversations', days],
    queryFn: async () => {
      const response = await api.get(`/analytics/conversations?days=${days}`)
      return response.data.data || []
    },
  })

  const { data: leadsData } = useQuery({
    queryKey: ['analytics-leads', days],
    queryFn: async () => {
      const response = await api.get(`/analytics/leads?days=${days}`)
      return response.data.data || []
    },
  })

  const { data: aiUsageData } = useQuery({
    queryKey: ['analytics-ai-usage', days],
    queryFn: async () => {
      const response = await api.get(`/analytics/ai-usage?days=${days}`)
      return response.data.data
    },
  })

  const stats = [
    { label: t('analytics.totalConversations'), value: dashboardData?.totalConversations || 0 },
    { label: t('analytics.totalLeads'), value: dashboardData?.totalLeads || 0 },
    { label: t('analytics.conversionRate'), value: `${dashboardData?.conversionRate || 0}%` },
    { label: t('analytics.avgMessages'), value: dashboardData?.avgMessagesPerConversation || 0 },
  ]

  const aiStats = aiUsageData?.summary ? [
    { label: t('analytics.aiRequests'), value: parseInt(aiUsageData.summary.total_requests) || 0 },
    { label: t('analytics.successRate'), value: `${aiUsageData.summary.total_requests > 0 ? Math.round((parseInt(aiUsageData.summary.successful_requests) / parseInt(aiUsageData.summary.total_requests)) * 100) : 0}%` },
    { label: t('analytics.avgLatency'), value: `${Math.round(parseFloat(aiUsageData.summary.avg_latency)) || 0}ms` },
    { label: t('analytics.totalTokens'), value: parseInt(aiUsageData.summary.total_tokens) || 0 },
  ] : [
    { label: t('analytics.aiRequests'), value: 0 },
    { label: t('analytics.successRate'), value: '0%' },
    { label: t('analytics.avgLatency'), value: '0ms' },
    { label: t('analytics.totalTokens'), value: 0 },
  ]

  const conversationsChart = conversationsData?.map((item: any) => ({
    date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    conversations: parseInt(item.count) || 0,
  })) || []

  const leadsByStatus = leadsData?.reduce((acc: any, item: any) => {
    acc[item.status] = (acc[item.status] || 0) + parseInt(item.count)
    return acc
  }, {}) || {}

  const pieData = Object.entries(leadsByStatus).map(([status, count]) => {
    const translated = t(`leads.${status}`)
    return {
      name: translated !== `leads.${status}` ? translated : status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
      value: count as number,
    }
  })

  const aiDailyChart = aiUsageData?.daily?.map((item: any) => ({
    date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    requests: parseInt(item.requests) || 0,
    tokens: parseInt(item.tokens) || 0,
    latency: Math.round(parseFloat(item.avg_latency)) || 0,
  })) || []

  const aiOperationData = aiUsageData?.byOperation?.map((item: any) => ({
    name: item.operation.charAt(0).toUpperCase() + item.operation.slice(1),
    value: parseInt(item.requests) || 0,
  })) || []

  const funnelData = [
    { name: t('analytics.totalConversations'), value: dashboardData?.totalConversations || 0 },
    { name: t('analytics.totalLeads'), value: dashboardData?.totalLeads || 0 },
    { name: t('analytics.funnelConverted'), value: dashboardData?.convertedConversations || 0 },
  ]

  const COLORS = ['#006cd4', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#6b7280']
  const AI_COLORS = ['#006cd4', '#10b981', '#f59e0b', '#ef4444']

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
        <h1 className="text-3xl font-bold">{t('analytics.title')}</h1>
        <div className="flex gap-2">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-4 py-2 rounded-lg transition-colors ${
                days === d
                  ? 'bg-[#006cd4] text-white'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              {d}{t('analytics.7d').slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-8 bg-[#1a1a25] p-1 rounded-lg w-fit">
        <button
          onClick={() => setActiveTab('general')}
          className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'general'
              ? 'bg-[#006cd4] text-white'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          {t('analytics.general')}
        </button>
        <button
          onClick={() => setActiveTab('ai')}
          className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'ai'
              ? 'bg-[#006cd4] text-white'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          {t('analytics.aiUsage')}
        </button>
      </div>

      {activeTab === 'general' ? (
        <>
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {stats.map((stat) => (
              <div key={stat.label} className="bg-[#12121a] border border-white/10 rounded-xl p-6">
                <div className="text-2xl font-bold">{stat.value}</div>
                <div className="text-sm text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Conversations Over Time */}
            <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-semibold mb-4">{t('analytics.conversationsOverTime')}</h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={conversationsChart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey="date" stroke="#71717a" fontSize={12} />
                    <YAxis stroke="#71717a" fontSize={12} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#12121a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                      labelStyle={{ color: '#a1a1aa' }}
                    />
                    <Line type="monotone" dataKey="conversations" stroke="#006cd4" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Leads by Status */}
            <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-semibold mb-4">{t('analytics.leadsByStatus')}</h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {pieData.map((_entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ backgroundColor: '#12121a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                      labelStyle={{ color: '#a1a1aa' }}
                      itemStyle={{ color: '#ffffff' }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Conversion Funnel */}
          <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
            <h2 className="text-lg font-semibold mb-4">{t('analytics.conversionFunnel')}</h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={funnelData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis dataKey="name" stroke="#71717a" fontSize={12} />
                  <YAxis stroke="#71717a" fontSize={12} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#12121a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                    itemStyle={{ color: '#ffffff' }}
                  />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {funnelData.map((_entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* AI Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {aiStats.map((stat) => (
              <div key={stat.label} className="bg-[#12121a] border border-white/10 rounded-xl p-6">
                <div className="text-2xl font-bold">{stat.value}</div>
                <div className="text-sm text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* AI Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* AI Requests Over Time */}
            <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-semibold mb-4">{t('analytics.aiRequestsOverTime')}</h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={aiDailyChart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey="date" stroke="#71717a" fontSize={12} />
                    <YAxis stroke="#71717a" fontSize={12} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#12121a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                      labelStyle={{ color: '#a1a1aa' }}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="requests" name={t('analytics.requestsLabel')} stroke="#006cd4" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="tokens" name={t('analytics.totalTokens')} stroke="#10b981" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Latency Chart */}
            <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-semibold mb-4">{t('analytics.avgLatencyMs')}</h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={aiDailyChart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey="date" stroke="#71717a" fontSize={12} />
                    <YAxis stroke="#71717a" fontSize={12} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#12121a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                    />
                    <Bar dataKey="latency" name={t('analytics.avgLatency')} fill="#f59e0b" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Model Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-semibold mb-4">{t('analytics.requestsByOperation')}</h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={aiOperationData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {aiOperationData.map((_entry: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={AI_COLORS[index % AI_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ backgroundColor: '#12121a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                      labelStyle={{ color: '#a1a1aa' }}
                      itemStyle={{ color: '#ffffff' }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Operation Breakdown */}
            <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-semibold mb-4">{t('analytics.operations')}</h2>
              <div className="space-y-4">
                {aiUsageData?.byOperation?.map((op: any) => (
                  <div key={op.operation} className="flex items-center justify-between bg-[#1a1a25] rounded-lg p-4">
                    <div>
                      <div className="font-medium text-white capitalize">{op.operation}</div>
                      <div className="text-sm text-gray-500">
                        {t('analytics.avgLatencyLabel', { ms: Math.round(parseFloat(op.avg_latency)) })}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-[#006cd4]">{op.requests}</div>
                      <div className="text-xs text-gray-500">{t('analytics.requestsLabel')}</div>
                    </div>
                  </div>
                )) || (
                  <div className="text-center py-8 text-gray-500">
                    {t('analytics.noAIUsage')}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
